import {
  SELLING_PRICE_KEYS,
  hasVehicleIdentifier,
  isSellingPriceMissingOrZero,
  mergeVehicleRecords,
  vehicleHasExplicitZeroSellingPrice,
  vehicleHasNoSellingPrice,
} from './vehicleSellingPrice'

/** ISO 3779 VIN — 17 chars, excludes I, O, Q */
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i

const MSRP_KEYS = ['msrp', 'MSRP']

export const isValidVinFormat = (vin) => typeof vin === 'string' && VIN_PATTERN.test(vin.trim())

const vehicleHasNoMsrp = (vehicle, embedVehicle) => {
  const merged = mergeVehicleRecords(vehicle, embedVehicle)

  for (const key of MSRP_KEYS) {
    if (!isSellingPriceMissingOrZero(merged[key])) {
      return false
    }
  }

  return hasVehicleIdentifier(merged)
}

const vehicleHasNegativeOrNonNumericPrice = (vehicle, embedVehicle) => {
  const merged = mergeVehicleRecords(vehicle, embedVehicle)

  for (const key of SELLING_PRICE_KEYS) {
    const raw = merged[key]

    if (raw === null || raw === undefined || raw === '') {
      continue
    }

    const n = Number(raw)

    if (!Number.isFinite(n) || n < 0) {
      return true
    }
  }

  return false
}

/**
 * Pre-flight vehicle checks for error-screen lead flow (new + used, including price-locked).
 * Does not run desking — catches price/VIN/inventory issues before normal registration.
 *
 * Price-locked new vehicles may legitimately have no selling price until after unlock;
 * absent selling price is only skipped for price-locked new. An explicit selling price of
 * zero always fails (new or used) because payments cannot run.
 *
 * @param {{
 *   vehicleData?: object | null,
 *   embedVehicle?: object | null,
 *   vehicleLoaded?: boolean,
 *   isPriceLocked?: boolean,
 * }} params
 * @returns {{ isValid: true } | { isValid: false, cause: string, message: string }}
 */
export const validateVehicleForDrFlow = ({
  vehicleData = null,
  embedVehicle = null,
  vehicleLoaded = false,
  isPriceLocked = false,
} = {}) => {
  const merged = mergeVehicleRecords(vehicleData, embedVehicle)
  const vin = merged.vin || embedVehicle?.vin
  const vehicleType = String(vehicleData?.type || embedVehicle?.type || '').toLowerCase()
  const skipMissingPriceCheck = isPriceLocked && vehicleType === 'new'

  if (!vin || !isValidVinFormat(vin)) {
    return {
      isValid: false,
      cause: 'invalid_vin',
      message: 'Vehicle VIN is invalid or unavailable.',
    }
  }

  if (vehicleLoaded && !vehicleData?.id && hasVehicleIdentifier(merged)) {
    return {
      isValid: false,
      cause: 'vehicle_not_in_inventory',
      message: 'Vehicle not in inventory',
    }
  }

  if (vehicleData?.id && vehicleHasNegativeOrNonNumericPrice(vehicleData, embedVehicle)) {
    return {
      isValid: false,
      cause: 'invalid_price',
      message: 'A valid selling price is unavailable for this vehicle.',
    }
  }

  if (vehicleData?.id && vehicleHasExplicitZeroSellingPrice(vehicleData, embedVehicle)) {
    return {
      isValid: false,
      cause: 'no_selling_price',
      message: 'A price is unavailable for this vehicle.',
    }
  }

  if (
    vehicleData?.id &&
    !skipMissingPriceCheck &&
    vehicleHasNoSellingPrice(vehicleData, embedVehicle)
  ) {
    return {
      isValid: false,
      cause: 'no_selling_price',
      message: 'A price is unavailable for this vehicle.',
    }
  }

  if (
    vehicleData?.id &&
    skipMissingPriceCheck &&
    vehicleHasNoSellingPrice(vehicleData, embedVehicle) &&
    vehicleHasNoMsrp(vehicleData, embedVehicle)
  ) {
    return {
      isValid: false,
      cause: 'no_selling_price',
      message: 'A price is unavailable for this vehicle.',
    }
  }

  return {isValid: true}
}

export const validateVehicleForDrFlowTestFunctions = {
  vehicleHasNegativeOrNonNumericPrice,
}
